import React, { useState, useEffect } from 'react';

interface AdminMetricsProps {
  stats: any;
}

const AdminMetrics: React.FC<AdminMetricsProps> = ({ stats }) => {
  const [realTimeStats, setRealTimeStats] = useState({
    conversionRate: 0,
    avgRegistrationsPerDay: 0,
    emailDeliveryRate: 0,
    growthTrend: '0%'
  });

  useEffect(() => {
    if (stats) {
      // Calcular métricas avanzadas
      const conversionRate = stats.total_registrations > 0 
        ? ((stats.emails_sent / stats.total_registrations) * 100).toFixed(1)
        : 0;
      
      const emailDeliveryRate = stats.total_registrations > 0
        ? ((stats.emails_sent / stats.total_registrations) * 100).toFixed(1)
        : 0;

      const avgRegistrationsPerDay = stats.total_registrations > 0
        ? (stats.total_registrations / 30).toFixed(1) // Asumiendo 30 días
        : 0;

      setRealTimeStats({
        conversionRate: parseFloat(conversionRate),
        avgRegistrationsPerDay: parseFloat(avgRegistrationsPerDay),
        emailDeliveryRate: parseFloat(emailDeliveryRate),
        growthTrend: stats.today_registrations > 0 ? '+' + Math.round((stats.today_registrations / stats.total_registrations) * 100) + '%' : '0%'
      });
    }
  }, [stats]);

  const MetricCard = ({ title, value, subtitle, icon, color, trend }: any) => (
    <div className={`bg-[#2B3139] rounded-lg p-4 shadow-sm border-l-4 ${color} hover:shadow-md hover:border-[#F0B90B] transition-all duration-300 transform hover:scale-105`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-[#848E9C]">{title}</p>
          <p className="text-2xl font-bold text-[#FFFFFF]">{value}</p>
          {subtitle && <p className="text-xs text-[#848E9C]">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color.replace('border-l-', 'bg-').replace('-500', '-100')}`}>
          {icon}
        </div>
      </div>
      {trend && (
        <div className="mt-2 flex items-center text-sm">
          <svg className="w-4 h-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span className="text-green-600 font-medium">{trend}</span>
          <span className="text-gray-500 ml-1">vs last period</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-[#2B3139] rounded-lg p-6 mb-6 border border-[#848E9C] border-opacity-20">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#FFFFFF] flex items-center">
          <svg className="w-5 h-5 text-[#F0B90B] mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
          Advanced Analytics
        </h3>
        <div className="flex items-center text-sm text-[#848E9C]">
          <div className="w-2 h-2 bg-[#02C076] rounded-full mr-2 animate-pulse"></div>
          Live Data
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          title="Conversion Rate"
          value={`${realTimeStats.conversionRate}%`}
          subtitle="Email signup rate"
          color="border-l-blue-500"
          trend={realTimeStats.growthTrend}
          icon={
            <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          }
        />

        <MetricCard
          title="Avg Daily Signups"
          value={realTimeStats.avgRegistrationsPerDay}
          subtitle="Last 30 days"
          color="border-l-green-500"
          icon={
            <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          }
        />

        <MetricCard
          title="Email Delivery"
          value={`${realTimeStats.emailDeliveryRate}%`}
          subtitle="Success rate"
          color="border-l-purple-500"
          icon={
            <svg className="w-6 h-6 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          }
        />



        <MetricCard
          title="Growth Trend"
          value={realTimeStats.growthTrend}
          subtitle="This month"
          color="border-l-pink-500"
          trend="+5.2%"
          icon={
            <svg className="w-6 h-6 text-pink-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
          }
        />
      </div>

      {/* Real Data Summary */}
      <div className="mt-6 bg-[#1E2329] rounded-lg p-4 shadow-sm border border-[#848E9C] border-opacity-20">
        <h4 className="text-sm font-medium text-[#FFFFFF] mb-3">Registration Summary</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#F0B90B]">{stats?.total_registrations || 0}</div>
            <div className="text-xs text-[#848E9C]">Total Users</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[#02C076]">{stats?.emails_sent || 0}</div>
            <div className="text-xs text-[#848E9C]">Emails Sent</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMetrics;